import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { apiFetch } from './ua/shared';
import OverviewSection from './ua/OverviewSection';
import AdvertisersSection from './ua/AdvertisersSection';
import AdvertiserDetail from './ua/AdvertiserDetail';
import CampaignDetail from './ua/CampaignDetail';
import ReferralSection from './ua/ReferralSection';
import OrganicSection from './ua/OrganicSection';

type Tab = 'overview' | 'advertisers' | 'referral' | 'organic';

interface AdvertiserStub { id: string; name: string }
interface CampaignStub { id: string; name: string; source_id: string | null }

/**
 * UA admin shell. Four top-level tabs; within Advertisers the shell also
 * manages drill-down state for advertiser → campaign detail views and
 * renders a breadcrumb so the user can step back up the hierarchy.
 */
export default function AdminUA() {
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedAdvertiserId, setSelectedAdvertiserId] = useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Small lookup queries so the breadcrumb can show human-readable names
  // without waiting on the full detail endpoint.
  const advertisersQ = useQuery({
    queryKey: ['admin', 'ua', 'sources'],
    queryFn: () => apiFetch('/api/ua/admin/sources') as Promise<{ sources: AdvertiserStub[] }>,
  });
  const campaignsQ = useQuery({
    queryKey: ['admin', 'ua', 'campaigns'],
    queryFn: () => apiFetch('/api/ua/admin/campaigns') as Promise<{ campaigns: CampaignStub[] }>,
  });

  const selectedAdvertiser = advertisersQ.data?.sources.find((a) => a.id === selectedAdvertiserId) ?? null;
  const selectedCampaign = campaignsQ.data?.campaigns.find((c) => c.id === selectedCampaignId) ?? null;

  // Overview table → campaign detail. Auto-switch to the Advertisers tab so
  // the breadcrumb surfaces Advertiser > Campaign correctly, and populate
  // the advertiser based on the campaign's source_id.
  function handleCampaignFromOverview(campaignId: string) {
    const c = campaignsQ.data?.campaigns.find((x) => x.id === campaignId) ?? null;
    setSelectedAdvertiserId(c?.source_id ?? null);
    setSelectedCampaignId(campaignId);
    setTab('advertisers');
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'advertisers', label: 'Advertisers' },
    { key: 'referral', label: 'Referral' },
    { key: 'organic', label: 'Organic' },
  ];

  // Build the breadcrumb for the Advertisers tab.
  const showBreadcrumb = tab === 'advertisers' && (selectedAdvertiserId || selectedCampaignId);
  const breadcrumb = showBreadcrumb ? (
    <nav className="flex items-center gap-1 text-sm text-slate-400">
      <button
        onClick={() => { setSelectedAdvertiserId(null); setSelectedCampaignId(null); }}
        className="hover:text-white"
      >
        Advertisers
      </button>
      {selectedAdvertiser && (
        <>
          <ChevronRight className="w-3 h-3" />
          <button
            onClick={() => setSelectedCampaignId(null)}
            className={selectedCampaignId ? 'hover:text-white' : 'text-white'}
          >
            {selectedAdvertiser.name}
          </button>
        </>
      )}
      {selectedCampaign && (
        <>
          <ChevronRight className="w-3 h-3" />
          <span className="text-white">{selectedCampaign.name}</span>
        </>
      )}
    </nav>
  ) : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">User Acquisition</h1>
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              setSelectedAdvertiserId(null);
              setSelectedCampaignId(null);
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-primary text-white' : 'text-slate-400 hover:text-white bg-slate-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {breadcrumb}

      {tab === 'overview' && <OverviewSection onSelectCampaign={handleCampaignFromOverview} />}

      {tab === 'advertisers' && !selectedAdvertiserId && !selectedCampaignId && (
        <AdvertisersSection onSelect={setSelectedAdvertiserId} />
      )}
      {tab === 'advertisers' && selectedAdvertiserId && !selectedCampaignId && (
        <AdvertiserDetail
          advertiserId={selectedAdvertiserId}
          onSelectCampaign={setSelectedCampaignId}
        />
      )}
      {tab === 'advertisers' && selectedCampaignId && (
        <CampaignDetail campaignId={selectedCampaignId} />
      )}

      {tab === 'referral' && <ReferralSection />}
      {tab === 'organic' && <OrganicSection />}
    </div>
  );
}
